import React, { useState, useEffect, useMemo } from "react";
import style from "./AnalyticsGraph.module.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { baseurl } from "../../Url";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const AnalyticsGraph = ({ learningModules = [] }) => {
  const [dateForLP, setDateForLP] = useState("week");
  const [lpModule, setLpModule] = useState("all");
  const [practicePaseData, setpracticePaseData] = useState([]);
  
  const [llModule, setLlModule] = useState("");
  const [l2Module, setL2Module] = useState("");
  const [moduleData, setModuleData] = useState([]); 
  const [modulesFromApi, setModulesFromApi] = useState([]); 
  const [selectedModule, setSelectedModule] = useState(""); 
  const [moduleWiseData, setModuleWiseData] = useState({ topics: [], scores: [] });

  const now = new Date();
  const currentMonth = now.toLocaleString("default", { month: "short" });
  const UserID = localStorage.getItem("USERID");
  const OrgID = localStorage.getItem("ORGID");
  const categoryId = localStorage.getItem("categoryID");

  const { chartLabels, chartData } = useMemo(() => {
    const labels = [];
    const data = [];

    const relevantData = Array.isArray(practicePaseData) && practicePaseData.length > 0 
                         ? practicePaseData[0] 
                         : {};

    if (relevantData.dates && relevantData.answered && Array.isArray(relevantData.dates) && Array.isArray(relevantData.answered)) {
      relevantData.dates.forEach((dateStr, index) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return; 

        let label;
        switch (dateForLP) {
          case "week":
            label = date.toLocaleString("default", { weekday: "short" }); 
            break;
          case "month":
            label = date.getDate();
            break;
          case "year":
            label = date.toLocaleString("default", { month: "short" }); 
            break;
          default:
            label = date.getDate();
        }

        labels.push(label);
        data.push(relevantData.answered[index] || 0); 
      });
    }
    return { chartLabels: labels, chartData: data };
  }, [practicePaseData, dateForLP]); 

  async function PracticePase(type) {
    try {
      const response = await fetch(`${baseurl}/practice/pase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: UserID,
          module_id: lpModule === "all" ? "" : lpModule,
          key: type,
          org_id: OrgID,
          category_id: categoryId,
        }),
      });

      const responseData = await response.json();
      console.log("API response:", responseData);

      if (responseData.status) {
        setpracticePaseData([responseData.data]); 
        setModulesFromApi(responseData.modules || []); 
      } else {
        setpracticePaseData([]);
        setModulesFromApi([]);
      }
    } catch (err) {
      console.error("API Error:", err);
      setpracticePaseData([]);
      setModulesFromApi([]);
    }
  }
 async function ModuleWiseScore(moduleId) {
  try {
    const response = await fetch(`${baseurl}/practice/module-wise-score`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: UserID,
        module_id: moduleId || "", // If moduleId is empty, API can return all
        org_id: OrgID,
        category_id: categoryId,
      }),
    });

    const responseData = await response.json();
    console.log("Module-wise API response:", responseData);

    if (responseData.status) {
      const { topics, scores } = responseData.data;
      setModuleWiseData({ topics, scores });

      // Normalize modules
      const normalizedModules = (responseData.modules || []).map((mod, idx) => ({
        module_id: mod.module_id || mod.id || idx,
        name: mod.name || `Module ${idx + 1}`,
      }));

      setModuleData(normalizedModules);
      setModulesFromApi(normalizedModules);

      // Set default selected module if none
      if (!moduleId && normalizedModules.length > 0) {
        setSelectedModule(normalizedModules[0].module_id); // Will trigger useEffect again
      }
    } else {
      setModuleWiseData({ topics: [], scores: [] });
      setModuleData([]);
      setModulesFromApi([]);
    }
  } catch (err) {
    console.error("Module-wise API Error:", err);
    setModuleWiseData({ topics: [], scores: [] });
    setModuleData([]);
    setModulesFromApi([]);
  }
}


  useEffect(() => {
    PracticePase(dateForLP);
  }, [dateForLP, lpModule]);

useEffect(() => {
  if (moduleData.length > 0 && !selectedModule) {
    setSelectedModule(moduleData[0].module_id);
  }
}, [moduleData,selectedModule]);

useEffect(() => {
  if (selectedModule) {
    ModuleWiseScore(selectedModule);
  } else {
    ModuleWiseScore("");
  }
}, [selectedModule]);


  useEffect(() => {
    if (moduleData.length > 0) {
      if (moduleData[0]) {
        setL2Module(moduleData[0].module_id);
      }
    }
  }, [modulesFromApi, lpModule, llModule]);

  const learningPaceChartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Questions answered",
        data: chartData,
        backgroundColor: "#FF8C00",
        borderRadius: 4,
        barPercentage: 0.6,
      },
    ],
  };

  const maxValue = chartData.length > 0 ? Math.max(...chartData, 0) : 0;
  let xAxisTitle = "";
  switch (dateForLP) {
    case "week":
      xAxisTitle = "Week";
      break;
    case "month":
      xAxisTitle = `${currentMonth}`;
      break;
    case "year":
      xAxisTitle = "Year";
      break;
    default:
      xAxisTitle = "Period";
  }

  const customTooltip = (context) => {
    const label = chartLabels[context.dataIndex] || '';
    const value = context.parsed.y || 0;
    return [
      `Questions Answered: ${value.toLocaleString()}`,
    ];
  };

  const learningPaceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: customTooltip, 
        },
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 4,
      },
    },
    scales: {
      x: {
        title: { 
          display: true, 
          text: xAxisTitle, 
          font: { size: 12, weight: "500" } 
        },
        grid: { display: false },
      },
      y: {
        min: 0,
        beginAtZero: true, 
        title: { 
          display: true, 
          text: "Questions Answered", 
          font: { size: 12, weight: "500" } 
        },
        suggestedMax: maxValue > 0 ? maxValue * 1.1 : 10, 
        grace: '10%', 
        ticks: { 
          stepSize: maxValue > 0 ? Math.max(1, Math.ceil(maxValue / 5)) : 1, 
          callback: (value) => value.toLocaleString(), 
        },
      },
    },
  };


  const topicLabels = moduleWiseData.topics || [];
  const topicScores = moduleWiseData.scores || [];

  const barColors = topicScores.map((score) => {
    if (score < 60) return "#E53935"; // Red for low scores
    if (score < 80) return "#FBC02D"; // Yellow for medium
    return "#00CFCF"; // Teal for high
  });

  const moduleWiseChartData = {
    labels: topicLabels.map((_, index) => `Topic ${index + 1}`),
    datasets: [
      {
        label: "Score",
        data: topicScores,
        backgroundColor: barColors,
        borderRadius: 6,
        barPercentage: 0.6,
      },
    ],
  };

  const moduleWiseOptions = {
    responsive: true,
    maintainAspectRatio: false,
plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: function(context) {
          const topicName = moduleWiseData.topics[context.dataIndex] || `Topic ${context.dataIndex + 1}`;
          const score = context.parsed.y;
          return `${topicName}: ${score}%`;  // Show name + score
        }
      },
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleColor: 'white',
      bodyColor: 'white',
      cornerRadius: 4,
    },
  },    scales: {
      x: {
        title: { display: true, text: "Topics", font: { size: 12, weight: "500" } },
        grid: { display: false },
      },
      y: {
        title: { display: true, text: "Score in %", font: { size: 12, weight: "500" } },
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 20, callback: (value) => value.toLocaleString() },
      },
    },
  };

  const handleLPDate = (type) => setDateForLP(type);
  useEffect(() => {
  if (learningModules.length > 0) {
    // For LP Dropdown
    if (lpModule === "all") {
      setLpModule("all");
    }

    // For Module-wise dropdown
    if (!selectedModule) {
      setSelectedModule(learningModules[0].module_id);
    }
  }
}, [learningModules]);


  return (
    <div className={style.learningSection}>
      <div className={style.learningSectionLeft}>
        <div className={style.learningHeader}>
          <p>Practice Pace</p>
        </div>
        <div className={style.learningSubHeader}>
          <div className={style.learningSubHeaderLeft}>
            {["week", "month", "year"].map((type) => (
              <div
                key={type}
                className={`${style.subHeaderCard} ${
                  dateForLP === type ? style.selectedSubHeaderCard : ""
                }`}
                onClick={() => handleLPDate(type)}
              >
                <p>{type.charAt(0).toUpperCase() + type.slice(1)}</p> 
              </div>
            ))}
          </div>
          <div className={style.learningSubHeaderRight}>
            <div className={style.moduleSelectWrap}>
              <select value={lpModule} onChange={(e) => setLpModule(e.target.value)}>
                <option value="all">All Modules</option>
                {learningModules.map((mod, idx) => (
                  <option key={mod.module_id} value={mod.module_id}>
                    {mod.name || `Module ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={style.barGraph} style={{ height: '300px' }}> 
          <Bar data={learningPaceChartData} options={learningPaceOptions} />
        </div>
      </div>

      {/* Right Section - Module-wise Score (similar structure: header, subheader with select, graph) */}
      <div className={style.learningSectionRight}>
        <div className={style.learningHeader}>
          <p>Module-wise score</p>
        </div>
        <div className={style.learningSubHeader}>
          <div className={style.learningSubHeaderRight}>
            <div className={style.moduleSelectWrap}>
              <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                {learningModules.map((mod, idx) => (
                  <option key={mod.module_id} value={mod.module_id}>
                    {mod.name || `Module ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={style.barGraph} style={{ height: '300px' }}>
          <Bar data={moduleWiseChartData} options={moduleWiseOptions} />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsGraph;
